import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PageHeader } from "@/components/EntityPage";
import { Image as ImageIcon, KeyRound, User } from "lucide-react";

function readFile(file) {
  return new Promise((resolve) => {
    const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(file);
  });
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "subadmin";
  const [s, setS] = useState({});
  const [me, setMe] = useState({});
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });

  async function load() {
    const [a, b] = await Promise.all([api.get("/settings"), api.get("/auth/me")]);
    setS(a.data); setMe(b.data);
  }
  useEffect(() => { load(); }, []);

  async function saveBranding(e) {
    e.preventDefault();
    try { await api.put("/settings", s); toast.success("Branding updated"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  async function uploadImage(key) {
    return async (e) => {
      const file = e.target.files?.[0]; if (!file) return;
      if (file.size > 800_000) return toast.error("Image too large (max ~800KB)");
      const url = await readFile(file);
      setS((cur) => ({ ...cur, [key]: url }));
    };
  }

  async function saveProfile(e) {
    e.preventDefault();
    try { const { data } = await api.patch("/auth/me", { name: me.name, phone: me.phone, photo_url: me.photo_url }); setMe(data); toast.success("Profile updated"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }

  async function pickPhoto(e) {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 800_000) return toast.error("Image too large (max ~800KB)");
    const url = await readFile(file);
    setMe((cur) => ({ ...cur, photo_url: url }));
  }

  async function changePw(e) {
    e.preventDefault();
    if (pw.next !== pw.confirm) return toast.error("New passwords do not match");
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPw({ current: "", next: "", confirm: "" }); toast.success("Password changed");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }

  return (
    <div>
      <PageHeader title="Settings" subtitle="Update your profile, change password, and manage brand assets."/>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-[hsl(224_45%_9%)] border-border">
          <div className="flex items-center gap-2 mb-4 text-amber-400"><User className="w-4 h-4"/><div className="text-xs uppercase tracking-[0.2em]">My profile</div></div>
          <form onSubmit={saveProfile} className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-slate-700 overflow-hidden grid place-items-center">
                {me.photo_url ? <img src={me.photo_url} alt="" className="w-full h-full object-cover"/> : <User className="w-8 h-8 text-slate-400"/>}
              </div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Photo</Label>
                <Input type="file" accept="image/*" onChange={pickPhoto} data-testid="me-photo" className="mt-1 bg-[hsl(224_40%_12%)]"/>
                <div className="text-[10px] text-muted-foreground mt-1">Max image size: <span className="text-amber-300">800 KB</span> · JPG/PNG/WEBP</div>
              </div>
            </div>
            <div><Label className="text-[10px] uppercase text-muted-foreground">Name</Label>
              <Input value={me.name || ""} onChange={(e) => setMe({ ...me, name: e.target.value })} data-testid="me-name" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
            <div><Label className="text-[10px] uppercase text-muted-foreground">Phone</Label>
              <Input value={me.phone || ""} onChange={(e) => setMe({ ...me, phone: e.target.value })} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
            <Button type="submit" data-testid="save-profile" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">Save profile</Button>
          </form>
        </Card>

        <Card className="p-6 bg-[hsl(224_45%_9%)] border-border">
          <div className="flex items-center gap-2 mb-4 text-amber-400"><KeyRound className="w-4 h-4"/><div className="text-xs uppercase tracking-[0.2em]">Change password</div></div>
          <form onSubmit={changePw} className="space-y-4">
            <div><Label className="text-[10px] uppercase text-muted-foreground">Current password</Label>
              <Input required type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
            <div><Label className="text-[10px] uppercase text-muted-foreground">New password</Label>
              <Input required type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-new" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
            <div><Label className="text-[10px] uppercase text-muted-foreground">Confirm new password</Label>
              <Input required type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
            <Button type="submit" data-testid="pw-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">Update password</Button>
          </form>
        </Card>

        {isAdmin && (
          <Card className="p-6 bg-[hsl(224_45%_9%)] border-border lg:col-span-2">
            <div className="flex items-center gap-2 mb-4 text-amber-400"><ImageIcon className="w-4 h-4"/><div className="text-xs uppercase tracking-[0.2em]">Branding · front images & logo</div></div>
            <form onSubmit={saveBranding} className="grid md:grid-cols-2 gap-4">
              <div><Label className="text-[10px] uppercase text-muted-foreground">Brand name</Label>
                <Input value={s.brand_name || ""} onChange={(e) => setS({ ...s, brand_name: e.target.value })} data-testid="brand-name" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
              <div><Label className="text-[10px] uppercase text-muted-foreground">Tagline</Label>
                <Input value={s.tagline || ""} onChange={(e) => setS({ ...s, tagline: e.target.value })} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Logo</Label>
                <div className="flex items-center gap-3 mt-1">
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-700 grid place-items-center">
                    {s.logo_url ? <img src={s.logo_url} alt="" className="w-full h-full object-cover"/> : <span className="text-xs">Logo</span>}
                  </div>
                  <Input type="file" accept="image/*" onChange={uploadImage("logo_url")} data-testid="brand-logo" className="bg-[hsl(224_40%_12%)]"/>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Max image size: <span className="text-amber-300">800 KB</span> · JPG/PNG/WEBP/SVG</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Login side image</Label>
                <div className="flex items-center gap-3 mt-1">
                  <div className="w-16 h-12 rounded overflow-hidden bg-slate-700">
                    {s.login_image_url ? <img src={s.login_image_url} alt="" className="w-full h-full object-cover"/> : null}
                  </div>
                  <Input type="file" accept="image/*" onChange={uploadImage("login_image_url")} data-testid="brand-login-img" className="bg-[hsl(224_40%_12%)]"/>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Max image size: <span className="text-amber-300">800 KB</span> · JPG/PNG/WEBP</div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase text-muted-foreground">Lottery hero image</Label>
                <div className="flex items-center gap-3 mt-1">
                  <div className="w-24 h-14 rounded overflow-hidden bg-slate-700">
                    {s.hero_image_url ? <img src={s.hero_image_url} alt="" className="w-full h-full object-cover"/> : null}
                  </div>
                  <Input type="file" accept="image/*" onChange={uploadImage("hero_image_url")} className="bg-[hsl(224_40%_12%)]"/>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Max image size: <span className="text-amber-300">800 KB</span> · JPG/PNG/WEBP</div>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" data-testid="save-branding" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">Save branding</Button>
              </div>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
